import React, { ReactElement, useMemo } from 'react'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { dynamicClassName } from 'styles/helper'
import { getAllSingletonComponentNames, getComponentTitle } from 'ui/uiConfig/factory'
import DEFAULT_DATASET_TEMPLATES from 'ui/uiConfig/template/datasets'
import { useSnapshot } from 'valtio'
import { CONFIG_MALFORMED_RELATIONS_MESSAGE, CONFIG_UNAVAILABLE_MESSAGE } from '../uiConfigDiagnosticsMessages'
import { buildRelationsTreeData } from './UiConfigRelationsTree.helpers'
import styles from './UiConfigRelationsTree.module.less'
import { ConfigNodeInfo, DsiTemplateInfo, RelationsTreeData } from './UiConfigRelationsTree.types'

type Props = {
  editingUid: UiConfigUid
}

type NodeRowProps = {
  node: ConfigNodeInfo
  icon: string
  extraBadges?: ReactElement[]
  onClick?: () => void
  isSelected?: boolean
  extraClassName?: string
  suppressAnchorBadge?: boolean
}

function NodeRow({
  node,
  icon,
  extraBadges,
  onClick,
  isSelected,
  extraClassName,
  suppressAnchorBadge,
}: NodeRowProps): ReactElement {
  const className = dynamicClassName({
    [styles.NodeRow]: true,
    [styles.NodeRow__clickable]: !!onClick,
    [styles.NodeRow__selected]: !!isSelected,
    [styles.NodeRow__anchor]: node.isAnchor && !isSelected,
    [styles.NodeRow__template]: node.isTemplate,
    [styles.NodeRow__unknown]: !node.config,
    [extraClassName ?? '']: !!extraClassName,
  })

  const title = node.config
    ? node.isTemplate
      ? 'Code template config (not directly editable)'
      : `component: ${node.componentType}`
    : 'Config not found in store'

  const content = (
    <>
      <i className={`fas fa-${icon} ${styles.NodeRow_Icon}`} />
      <div className={styles.NodeRow_Content}>
        <div className={styles.NodeRow_Label}>{node.displayName}</div>
        {node.componentType && <div className={styles.NodeRow_Meta}>{node.componentType}</div>}
      </div>
      <div className={styles.NodeRow_Badges}>
        {node.isAnchor && !suppressAnchorBadge && (
          <span className={`${styles.Badge} ${styles.Badge__anchor}`}>anchor</span>
        )}
        {node.isTemplate && <span className={`${styles.Badge} ${styles.Badge__template}`}>template</span>}
        {extraBadges}
      </div>
    </>
  )

  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick} title={title}>
        {content}
      </button>
    )
  }

  return (
    <div className={className} title={title}>
      {content}
    </div>
  )
}

function getBaseConfigBadges({
  usesDefault,
  hasBaseItems,
}: {
  usesDefault: boolean
  hasBaseItems: boolean
}): ReactElement[] {
  return [
    <span key="bc" className={`${styles.Badge} ${styles.Badge__info}`}>
      system
    </span>,
    ...(usesDefault
      ? [
          <span key="ud" className={`${styles.Badge} ${styles.Badge__success}`}>
            use_default
          </span>,
        ]
      : []),
    ...(hasBaseItems
      ? [
          <span key="bi" className={`${styles.Badge} ${styles.Badge__success}`}>
            base_items
          </span>,
        ]
      : []),
  ]
}

function getDsiBadge({ resolvedFrom }: DsiTemplateInfo): { label: string; className: string } {
  if (resolvedFrom === 'base_config') {
    return { label: 'base_config', className: styles.Badge__success }
  }

  if (resolvedFrom === 'code_template') {
    return { label: 'code template', className: styles.Badge__template }
  }

  return { label: 'unresolved', className: styles.Badge__info }
}

function DsiTemplates({ templateDsis }: { templateDsis: DsiTemplateInfo[] }): ReactElement | null {
  if (templateDsis.length === 0) {
    return null
  }

  return (
    <div className={styles.DsiTemplates}>
      <div className={styles.DsiTemplates_Label}>
        <i className="fas fa-layer-group" />
        {` DSI templates (${templateDsis.length})`}
      </div>
      {templateDsis.map((dsi, index) => {
        const badge = getDsiBadge(dsi)
        return (
          <div key={`${dsi.return_id}-${index}`} className={styles.DsiTemplates_Item}>
            <code>{dsi.return_id}</code>
            <span className={`${styles.Badge} ${badge.className}`}>{badge.label}</span>
          </div>
        )
      })}
    </div>
  )
}

export default function UiConfigRelationsTree({ editingUid }: Props): ReactElement {
  const snap = useSnapshot(uiConfigStore)
  const isMissingConfig = !snap.uiConfigs[editingUid]

  const singletonNames = useMemo(() => new Set<string>(getAllSingletonComponentNames()), [])
  const codeTemplateDsiReturnIds = useMemo(() => new Set<string>(Object.keys(DEFAULT_DATASET_TEMPLATES)), [])

  const relations = useMemo<RelationsTreeData | null>(() => {
    const parsedChildrenIds = uiConfigStore.getParsedUiConfig(editingUid)?.children_ids ?? []
    const unparsedConfig = uiConfigStore.getUnparsedUiConfig(editingUid)
    const idToUiConfig = snap.idToUiConfig
    const getTitle = (componentType: string): string | null => getComponentTitle(componentType)

    return buildRelationsTreeData({
      editingUid,
      uiConfigs: snap.uiConfigs,
      idToUiConfig,
      baseUiConfig: snap.baseUiConfig,
      parsedChildrenIds,
      unparsedConfig,
      singletonNames,
      getTitle,
      codeTemplateDsiReturnIds,
    })
  }, [editingUid, snap.uiConfigs, snap.baseUiConfig, snap.idToUiConfig, singletonNames, codeTemplateDsiReturnIds])

  if (isMissingConfig) {
    return <div className={styles.Empty}>{CONFIG_UNAVAILABLE_MESSAGE}</div>
  }

  if (!relations) {
    return <div className={styles.Empty}>{CONFIG_MALFORMED_RELATIONS_MESSAGE}</div>
  }

  const { ancestors, selectedNode, children, usesBaseConfig, hasBaseItems, usesDefault, templateDsis, baseConfigNode } =
    relations

  function navigateTo(uid: number): void {
    uiConfigStore.setEditingUid(uid, {
      confirmDiscardDraft: () => window.confirm('Discard unsaved draft and switch to selected config?'),
    })
  }

  const hasAnyRelations = ancestors.length > 0 || usesBaseConfig || children.length > 0 || templateDsis.length > 0

  return (
    <div className={styles.UiConfigRelationsTree}>
      {ancestors.length > 0 && (
        <section className={styles.Section}>
          <div className={styles.SectionLabel}>Referenced by</div>
          {ancestors.map((node) => (
            <NodeRow
              key={node.id}
              node={node}
              icon="arrow-up"
              onClick={node.isTemplate ? undefined : () => navigateTo(node.uid)}
            />
          ))}
          <div className={styles.Connector} />
        </section>
      )}

      {usesBaseConfig && baseConfigNode && (
        <section className={styles.Section}>
          <div className={styles.SectionLabel}>Base config applied</div>
          <NodeRow
            node={baseConfigNode}
            icon="database"
            extraClassName={styles.NodeRow__base}
            suppressAnchorBadge
            extraBadges={getBaseConfigBadges({ usesDefault, hasBaseItems })}
            onClick={baseConfigNode.isTemplate ? undefined : () => navigateTo(baseConfigNode.uid)}
          />
          <div className={styles.Connector} />
        </section>
      )}

      <section className={styles.Section}>
        <div className={styles.SectionLabel}>Selected</div>
        <NodeRow node={selectedNode} icon="dot-circle" isSelected />
        <DsiTemplates templateDsis={templateDsis} />
      </section>

      {children.length > 0 && (
        <section className={styles.Section}>
          <div className={styles.Connector} />
          <div className={styles.SectionLabel}>Children ({children.length})</div>
          {children.map((node, i) => (
            <NodeRow
              key={`${node.id}-${i}`}
              node={node}
              icon="arrow-down"
              extraBadges={
                node.isDefaultTemplateChild && !node.isTemplate
                  ? [
                      <span key="dt" className={`${styles.Badge} ${styles.Badge__template}`}>
                        use_default
                      </span>,
                    ]
                  : undefined
              }
              onClick={node.config && !node.isTemplate ? () => navigateTo(node.uid) : undefined}
            />
          ))}
        </section>
      )}

      {!hasAnyRelations && <div className={styles.Empty}>No relations found for this config.</div>}
    </div>
  )
}
